import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { keepPreviousData } from '@tanstack/react-query';

import { AppID, Environment } from '@playq/octopus-common';
import { GetTemplatesResult } from '@playq/octopus2-tgservices';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { ITableQuery } from '/shared/Table/interfaces';
import { appToolkit } from '/store';

const emptyArray: [] = [];
const defaultOffsetLimit = new OffsetLimit({ offset: 0, limit: 10 });

export const teamsSupportQueryKeys: unknown[] = ['teams', 'service', 'support', 'templates'];

export const useGetTemplates = (
  env: Environment,
  id: string | undefined,
  offsetLimit: OffsetLimit = defaultOffsetLimit,
  query?: ITableQuery<string>,
  options?: IUseQueryOptionsExtended<GetTemplatesResult | undefined, GenericFailure | Error>
) => {
  const appID: AppID | undefined = useSelector(appToolkit.selectors.appID);

  const keys = useMemo(
    () => teamsSupportQueryKeys.concat(appID, offsetLimit.offset, offsetLimit.limit, env, query),
    [appID, offsetLimit, env, query]
  );

  const isValid = useMemo(() => appID !== undefined, [appID]);
  const enabled = useMemo(() => (options?.enabled ?? true) && isValid, [options?.enabled, isValid]);
  const enablePrefetch = useMemo(
    () => (options?.enablePrefetch ?? true) && isValid,
    [options?.enablePrefetch, isValid]
  );

  const res = useEitherQuery(keys, () => services2.teamsLookupService.getTemplates(appID as AppID, offsetLimit, env), {
    placeholderData: keepPreviousData,
    ...options,
    enabled,
  });

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);
  const selectedTemplate = useMemo(
    () => res.data?.templates.find((template) => template.id.id === id),
    [id, res.data?.templates]
  );

  useQueryPrefetch({
    keys,
    enabled: enablePrefetch,
    total,
    args: [appID as AppID, offsetLimit, env],
    serviceMethod: services2.teamsLookupService.getTemplates.bind(services2.teamsLookupService),
  });

  return {
    ...res,
    selectedTemplate,
    templates: res.data?.templates || emptyArray,
    total,
    queryKeys: keys,
  };
};
